/**
 * MF Slot Web Component
 *
 * Lifecycle:
 * - Emits `mf-slot:connected` once both `domain` and `name` attributes are
 *   present AND the element is connected to the DOM.
 * - Emits `mf-slot:disconnected` whenever the element leaves the DOM.
 *
 * The previous `connectedCallback` + `await setTimeout(0)` design had two
 * problems:
 *   1. Under fast navigation Angular could update the same element's attrs
 *      after the macrotask fired, leading to mismatched (domain, name) reads.
 *   2. It forced a macrotask delay even when the attributes were already set
 *      synchronously, opening windows where the element stayed unbound.
 *
 * The new flow uses `static observedAttributes` so any setAttribute call by
 * Angular's renderer triggers `attributeChangedCallback`. We schedule a
 * single microtask emit when both attrs are present, which yields exactly
 * one step to let Angular finish its synchronous update block (props like
 * `mf-cb-*` and `mf-data-*`, plus the `props` attribute) before we read.
 *
 * This element does not expose any public state on itself — communication
 * with the shell SDK is exclusively via the `mf-slot:connected` and
 * `mf-slot:disconnected` custom events.
 */

import { logger } from '@mf-tools/cli-common';

const log = logger.browser('mf-slot');

export class MFSlotElement extends HTMLElement {
  // Tell the platform which attributes to observe. Without this, attribute
  // writes by Angular do not fire attributeChangedCallback.
  static get observedAttributes(): string[] {
    return ['domain', 'name'];
  }

  // Per-connection emit guard. Reset on disconnect so that a slot which is
  // detached and reattached (e.g. moved in the DOM) re-emits on reattach.
  private _emitted = false;
  private _emitScheduled = false;

  connectedCallback(): void {
    log.info('[MFSlot] connectedCallback fired for', this.id || 'unnamed slot');
    this._scheduleEmit();
  }

  disconnectedCallback(): void {
    log.info('[MFSlot] disconnectedCallback fired for', this.id || 'unnamed slot');

    // Reset so a future reconnect can re-emit.
    this._emitted = false;

    document.dispatchEvent(new CustomEvent('mf-slot:disconnected', {
      detail: {
        element: this,
        id: this.id
      },
      bubbles: true,
      composed: true
    }));
  }

  attributeChangedCallback(_name: string, _oldVal: string | null, _newVal: string | null): void {
    // Either domain or name changed. If both are now set and the element is
    // attached, schedule a single emit. We emit at most once per connection.
    this._scheduleEmit();
  }

  private _scheduleEmit(): void {
    if (this._emitted || this._emitScheduled) return;
    if (!this.isConnected) return;
    if (!this.getAttribute('domain') || !this.getAttribute('name')) return;

    this._emitScheduled = true;
    // queueMicrotask yields exactly one step so Angular's synchronous update
    // block finishes (props bindings like mf-cb-*, mf-data-*, attr.props) but
    // does not push us behind unrelated macrotasks.
    queueMicrotask(() => {
      this._emitScheduled = false;
      this._emit();
    });
  }

  private _emit(): void {
    if (this._emitted) return;
    if (!this.isConnected) return;

    const domain = this.getAttribute('domain');
    const name = this.getAttribute('name');
    if (!domain || !name) return;

    this._emitted = true;

    const mfCbs: Record<string, unknown> = {};
    const mfData: Record<string, unknown> = {};
    for (const key of Object.keys(this)) {
      if (key.startsWith('mf-cb-')) {
        const cbName = key.slice('mf-cb-'.length);
        mfCbs[cbName] = (this as any)[key];
      } else if (key.startsWith('mf-data-')) {
        const dataName = key.slice('mf-data-'.length);
        try {
          mfData[dataName] = JSON.parse((this as any)[key]);
        } catch {
          mfData[dataName] = (this as any)[key];
        }
      }
    }

    const propsAttr = this.getAttribute('props');
    let baseProps: Record<string, unknown> = {};
    if (propsAttr) {
      try {
        baseProps = JSON.parse(propsAttr);
      } catch {
        baseProps = {};
      }
    }

    this.dispatchEvent(new CustomEvent('mf-slot:connected', {
      detail: {
        element: this,
        id: this.id,
        domain,
        name,
        props: {
          ...baseProps,
          ...mfCbs,
          ...mfData
        }
      },
      bubbles: true,
      composed: true
    }));
  }
}
